<template>
    <div class="new-edit-assumptions">
        <workflow-layout>
            <template v-slot:header>
                <workflow-header
                    mwc-id="workflow-header"
                    :key="$route.params.portfolioNumber + 'header'"
                    :component-config="headerConfig"
                    v-on="events"
                ></workflow-header>
            </template>
            <portfolio-actions :mwc-id="getMwcIdForChild('portfolioActions')" :portfolioIndex="$route.params.portfolioNumber"></portfolio-actions>
            <div :class="titleSectionClass">
                {{ hypoType === 2 ? getUILabel("review_and_edit_assumptions_investments") : getUILabel("review_and_edit_assumptions") }}
                {{ hypoType !== 2 ? getPortfolioNumber($route.params.portfolioNumber) : "" }}
            </div>
            <div class="sync-actions-panel" v-if="hypoType === 1">
                <div v-if="isPortfolio2Synced && portfolioNumber === '1'">
                    <mds-icon class="synced-mark" name="check"></mds-icon> {{ getUILabel("synced_portfolio") }}
                </div>
                <div v-if="!isPortfolio2Synced && portfolioNumber === '1'">
                    <mds-button variation="secondary" @click="syncPortfolio"> {{ getUILabel("sync_portfolio") }} </mds-button>
                </div>
            </div>
            <edit-assumptions
                :mwc-id="getMwcIdForChild('editAssumptions')"
                :key="$route.params.portfolioNumber + '-edit'"
                @assumptions-notification="handleNotification"
            ></edit-assumptions>
        </workflow-layout>
        <header-dialog
            :mwc-id="getMwcIdForChild('headerDialog')"
            :show="showDialog"
            :headerDialogConfig="dialogConfig"
            @dialog-close="dialogCloseHandler"
        ></header-dialog>
        <save-dialog :mwc-id="getMwcIdForChild('saveDialog')" :show="showSaveDialog" @save-event="handleSaveDialog"></save-dialog>
        <mds-alert
            v-show="!validateHoldingsTimePeriod && errorStack.length === 0"
            class="alert"
            variation="error"
            size="medium"
            tinted
            :persistent="false"
            :title="getUILabel('ALERT_EDIT_ASSUMPTIONS_DIFFTIMEPERIOD_TITLE')"
            role="alertdialog"
        >
            {{ getUILabel("ALERT_EDIT_ASSUMPTIONS_DIFFTIMEPERIOD_MSG") }}
        </mds-alert>
        <mds-dialog :actionRequired="true" v-model="validateCommonEndDate" :title="getUILabel('ALERT_EDIT_ASSUMPTIONS_DIFFCOMMONTIMEPERIOD_TITLE')">
            {{ getUILabel("ALERT_EDIT_ASSUMPTIONS_DIFFCOMMONTIMEPERIOD_MSG") }}
            <template v-slot:mds-dialog-actions-right>
                <mds-button-container right-aligned>
                    <mds-button type="button" variation="primary" @click="goBackToPortfolio">{{
                        getUILabel("ALERT_EDIT_ASSUMPTIONS_DIFFCOMMONTIMEPERIOD_ACTION")
                    }}</mds-button>
                </mds-button-container>
            </template>
        </mds-dialog>
    </div>
</template>

<script>
import MwcSharedMixin from "@/mixins/MwcSharedMixin";
import HypoWorkflowMixin from "@/mixins/HypoWorkflowMixin";
import HeaderConfigMixin from "@/mixins/HeaderConfigMixin";
import * as types from "@/store/mutation-types.js";
import EditAssumptions from "../EditAssumptions";
import WorkflowLayout from "../layouts/WorkflowLayout";
import WorkflowHeader from "hypo-workflow-header/src/WorkflowHeader";
import PortfolioActions from "./PortfolioActions";
import HeaderDialog from "./HeaderDialog";
import SaveDialog from "./SaveDialog";
import MdsDialog from "@mds/dialog";
import MdsAlert from "@mds/alert";
import MdsIcon from "@mds/icon";
import { MdsButton, MdsButtonContainer } from "@mds/button";
import { isEqualTimePeriod, isValidCommonEndDate, checkSyncedPortfolios } from "@/util.js";

export default {
    name: "new-edit-assumptions",
    mixins: [MwcSharedMixin, HypoWorkflowMixin, HeaderConfigMixin],
    components: {
        WorkflowLayout,
        WorkflowHeader,
        EditAssumptions,
        HeaderDialog,
        SaveDialog,
        PortfolioActions,
        MdsAlert,
        MdsIcon,
        MdsButton,
        MdsButtonContainer,
        MdsDialog
    },
    data() {
        return {
            events: {
                continue: () => this.continueHandler(this),
                back: () => this.backHandler(this),
                close: () => this.closeHandler(this),
                closeNotSave: () => this.closeNotSaveHandler(this),
                rename: () => this.renameHandler(this),
                delete: () => this.deleteHandler(this)
            },
            showDialog: false,
            showSaveDialog: false,
            dialogConfig: {},
            portfolioNumber: this.$route.params.portfolioNumber,
            errorCheck: {
                rebalance: false,
                benchmark: false
            },
            hasError: false,
            errorStack: [],
            hypoType: null
        };
    },
    beforeMount() {
        this.hypoType = parseInt(this.$store.getters["hypo/getType"]);
        this.dialogConfig = this.defaultDialogConfig;
        this.setEditingPortfolio(this.portfolioNumber);
    },
    watch: {
        "$route.params.portfolioNumber": {
            handler(newVal) {
                this.portfolioNumber = newVal;
                this.setEditingPortfolio(newVal);
            }
        }
    },
    methods: {
        checkAssumptionsError(error) {
            this.errorCheck[error.key] = !!error.payload;
            return !Object.values(this.errorCheck).every(item => !item);
        },
        handleNotification(error) {
            const { payload, key } = error;
            this.hasError = this.checkAssumptionsError(error);
            if (this.hasError && this.errorCheck[key]) {
                const notification = {
                    key,
                    visible: true,
                    size: "medium",
                    variation: "error",
                    title: payload.title,
                    persistent: false,
                    text: payload.text,
                    duration: 0
                };
                if (!this.errorStack.some(item => item.key === key)) {
                    this.errorStack.push(notification);
                    this.$store.dispatch("notifications/showNotificationStack", this.errorStack);
                }
            } else if (!this.errorCheck[key]) {
                this.errorStack = this.errorStack.filter(item => item.key !== key);
                this.$store.dispatch("notifications/showNotificationStack", this.errorStack);
            } else {
                this.$store.dispatch("notifications/clearNotificationStack");
            }
        },
        setEditingPortfolio(index) {
            const { portfolioList } = this.$store.state.investments;
            this.$store.commit(`investments/${types.SET_EDITING_PORTFOLIO_ID}`, portfolioList[index].portfolioId);
            const portfolio = portfolioList[index];
            if (parseInt(index) === 1 && "synced" in portfolio && !portfolio.firstSynced) {
                this.$store.dispatch("investments/syncPortfolioAssumptions");
            }
        },
        canSync(portfolio) {
            const isFirstSync = "synced" in portfolio && !portfolio.firstSynced;
            const isPortfolio2 = this.portfolioNumber === "1" && !this.isPortfolio2Synced;
            return isFirstSync || isPortfolio2;
        },
        syncPortfolio() {
            const portfolio = this.$store.state.investments.portfolioList[1];
            if (this.canSync(portfolio)) {
                this.$store.dispatch("investments/syncPortfolioAssumptions");
            }
        },
        continueHandler() {
            this.saveLastStep(this.currentStep.keyString);
            this.$router.push(this.currentStep.nextRoute.route);
        },
        closeHandler() {
            const hypoType = this.hypoType;
            if (this.portfolioNumber === "0" && hypoType === 1) {
                this.syncPortfolio();
            }
            this.saveLastStep(this.currentStep.keyString);
            this.checkHypoCount();
        },
        closeNotSaveHandler(instance) {
            this.$store.commit(`hypo/${types.SET_FIRM_SAVED}`, false);
            this.$store.dispatch("clearHypoInput");
            this.$router.push("/type");
        },
        backHandler() {
            this.saveLastStep(this.currentStep.keyString);
            this.$router.push(this.currentStep.previousRoute);
        },
        renameHandler() {
            this.dialogConfig = this.getDialogConfig("rename", "hypo", null);
            this.showDialog = true;
        },
        deleteHandler() {
            this.dialogConfig = this.getDialogConfig("delete", "hypo", null);
            this.showDialog = true;
        },
        goBackToPortfolio() {
            this.$router.push(this.currentStep.previousRoute);
        }
    },
    computed: {
        titleSectionClass() {
            return this.hypoType === 2 ? "section-title section-title--top-gap" : "section-title";
        },
        validateHoldingsTimePeriod() {
            const { portfolioList } = this.$store.state.investments;
            if (portfolioList.length > 1 && this.portfolioNumber === "1") {
                return isEqualTimePeriod(portfolioList);
            }
            return true;
        },
        validateCommonEndDate: {
            get() {
                const { portfolioList } = this.$store.state.investments;
                return !isValidCommonEndDate(portfolioList) && this.errorStack.length === 0;
            },
            set(value) {}
        },
        isPortfolio2Synced() {
            if (this.hypoType === 0) {
                return false;
            } else {
                const { portfolioList } = this.$store.state.investments;
                return checkSyncedPortfolios([portfolioList[0], portfolioList[1]]);
            }
        },
        headerConfig() {
            const buttonState = this.validateHoldingsTimePeriod && !this.hasError;
            return {
                leftButton: {
                    type: "secondary",
                    isActive: buttonState,
                    isVisible: true,
                    label: this.$store.state.hypo.firmSaved ? this.getUILabel("COMMON_CLOSE") : this.getUILabel("COMMON_SAVE_AND_CLOSE"),
                    actionTrigger: this.$store.state.hypo.firmSaved ? "closeNotSave" : "close"
                },
                middleButton: {
                    type: "secondary",
                    isActive: buttonState,
                    isVisible: true,
                    label: this.getUILabel("COMMON_PREVIOUS"),
                    actionTrigger: "back"
                },
                rightButton: {
                    type: "primary",
                    isActive: buttonState,
                    isVisible: true,
                    label: this.getUILabel("COMMON_NEXT"),
                    actionTrigger: "continue"
                },
                ...this.getTitleAndStepperConfig()
            };
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~@mds/typography";
@import "@mds/icon/src/icon_mixins";
.new-edit-assumptions {
    .section-title {
        @include mds-level-6-heading($bold: false);
        padding-bottom: $mds-space-1-x;
        color: $mds-text-color-primary;
        border-bottom: $mds-border-separator;
        &--top-gap {
            padding: $mds-space-1-x;
        }
    }
    .sync-actions-panel {
        max-width: 1000px;
        margin: 0 auto;
        padding: 10px 0;
    }
    .synced-mark {
        vertical-align: middle;
        @include mds-icon-color($mds-feedback-color-success);
    }
    & .alert {
        position: fixed;
        top: 20px;
        width: 600px;
        left: 30%;
        z-index: 12;
        ::v-deep .mds-alert__dismiss-button___hypo {
            display: none;
        }
    }
}
</style>
